import { nil } from '@fullstory/typeutils';
import { theDummyDoc } from './dummyDoc';
import {
  CSSPrimitiveType,
  CSSPrimitiveValue,
  CSSStyleDeclarationWithCSSValue,
  CSSValueType,
} from './cssvalue';

// Returns elem.sheet as a CSSStyleSheet. Returns undefined if elem is null/undefined.
export function elemSheet(
  elem: HTMLLinkElement | HTMLStyleElement | undefined,
): CSSStyleSheet | nil {
  return elem ? (elem.sheet as CSSStyleSheet) : undefined;
}

// Returns sheet.[css]rules. Returns undefined if sheet is null/undefined.
export function sheetRules(sheet: CSSStyleSheet | nil): CSSRuleList | undefined {
  try {
    return sheet ? sheet.cssRules || sheet.rules : undefined;
  } catch (e) {
    // Security error for cross-domain sheets.
    return undefined;
  }
}

let quoteCheckElement: HTMLDivElement | undefined;

// Given a declaration and property name, this function detects if the property
// value is a string value with missing quotes, and returns the correctly quoted
// value. For other kinds of property values, this function returns undefined.
//
// The reason we need to do this is that Safari's implementation of
// getPropertyValue() doesn't quote string values reliably, yielding invalid CSS
// that doesn't work correctly during playback.  Fortunately, it supports an
// obsolete API, getPropertyCSSValue(), that lets us detect string values and
// add quotes as needed.
//
// *Unfortunately*, getPropertyCSSValue() itself is buggy, and sometimes reports
// that identifier values are string values. (e.g., in `grid-row-start: nav`,
// Safari wrongly tells us that `nav` is a string value.) If we add quotes to those
// identifier values, then we again end up with invalid CSS! This means that we
// can't blindly add quotes; we need to validate the result.
function getQuotedStringlikeValueIfNeeded(
  block: CSSStyleDeclaration,
  name: string,
): string | undefined {
  const declaration = block as CSSStyleDeclarationWithCSSValue;
  if (typeof declaration.getPropertyCSSValue !== 'function') {
    return undefined;
  }

  const cssValue = declaration.getPropertyCSSValue(name);
  if (cssValue == null) {
    return undefined; // Property lookup failed.
  }

  let cssPrimitiveValue: CSSPrimitiveValue;
  switch (cssValue.cssValueType) {
    case CSSValueType.CSS_PRIMITIVE_VALUE:
      cssPrimitiveValue = cssValue;
      break;

    case CSSValueType.CSS_VALUE_LIST: {
      // Value lists are tricky in the general case; for now, we'll punt on them
      // unless the list contains exactly one primitive value.
      if (cssValue.length !== 1) {
        return undefined;
      }
      const wrappedValue = cssValue.item(0);
      if (wrappedValue == null) {
        return undefined; // Shouldn't actually happen since we checked the length.
      }
      if (wrappedValue.cssValueType !== CSSValueType.CSS_PRIMITIVE_VALUE) {
        return undefined;
      }
      cssPrimitiveValue = wrappedValue;
      break;
    }
    default:
      return undefined;
  }

  if (cssPrimitiveValue.primitiveType !== CSSPrimitiveType.CSS_STRING) {
    return undefined; // The value isn't a string or a stringlike identifier.
  }

  const dummyDoc = theDummyDoc();
  if (!quoteCheckElement) {
    quoteCheckElement = dummyDoc.createElement('div');
  }

  // Try quoting the value and make sure the result is valid CSS.
  const serializedValue = cssPrimitiveValue.cssText;
  try {
    quoteCheckElement.style.cssText = `${name}: "${serializedValue}";`;
    const quoteCheckDeclaration = quoteCheckElement.style as CSSStyleDeclarationWithCSSValue;
    const quoteCheckCSSValue = quoteCheckDeclaration.getPropertyCSSValue!(name);
    if (quoteCheckCSSValue == null) {
      return undefined; // Don't add quotes; the result doesn't parse.
    }
    const reserializedValue = quoteCheckCSSValue.cssText;
    if (serializedValue !== reserializedValue) {
      return undefined; // Don't add quotes; doing so changes the result.
    }
  } catch (_) {
    return undefined; // Something went wrong; be conservative and don't add quotes.
  } finally {
    quoteCheckElement.style.cssText = '';
  }

  // It looks like adding quotes produces the right serialization; add them.
  return `"${serializedValue}"`;
}

// returns true iff the input string is wrapped in matching quotes (single or double)
// this should always return true for a string output of getQuotedStringlikeValueIfNeeded
export function isStringWrappedInQuotes(input: string) {
  return (input[0] === '"' || input[0] === "'") && input[input.length - 1] === input[0];
}

export function getCssPropertyValue(block: CSSStyleDeclaration, name: string): string {
  // Safari sometimes fails to add quotes when serializing stringlike CSS
  // values. Check if we're hitting that case and, if so, return a custom
  // serialization instead of the default one. Note that there is a feature
  // check inside this function, so we'll short-circuit the check on browsers
  // where there isn't an issue.
  const quotedValue = getQuotedStringlikeValueIfNeeded(block, name);
  if (quotedValue !== undefined) {
    return quotedValue;
  }
  return block.getPropertyValue(name);
}
